import { CONSOLE_PATHS, consoleRoutes, type PermissionKey } from "@sagot/constants";
import { Navigate, type RouteObject } from "react-router";
import { AcceptInvitePage } from "../auth/accept-invite-page";
import { ForgotPasswordPage } from "../auth/forgot-password-page";
import { LoginPage } from "../auth/login-page";
import { RegisterPage } from "../auth/register-page";
import { ResetPasswordPage } from "../auth/reset-password-page";
import { TwoFactorPage } from "../auth/two-factor-page";
import { VerifyEmailPage } from "../auth/verify-email-page";
import { OnboardingPage } from "../onboarding/onboarding-page";
import { RequirePermission } from "../pages/forbidden-page";
import { GatedPlaceholder, NotFoundPage } from "../pages/placeholder-pages";
import { RolesPage } from "../roles/roles-page";
import { HomeRedirect, PublicOnly, RequireAuth } from "../session/guards";
import { WorkspaceGate } from "../session/workspace-context";
import { ConsoleLayout, type ConsoleRouteHandle } from "../shell/console-layout";
import { META_CALLBACK_PATH } from "../features/deploy-knowledge/meta-callback/meta-callback-page";
import { featureScreens } from "../features/registry";
import { knowledgeGraphRoutes } from "../features/knowledge-graph/knowledge-graph-routes";
import { serviceRoutes } from "../features/service/service-routes";
import { connectorRoutes } from "../features/workspace/settings/connectors/connectors-routes";
import { GOOGLE_CALLBACK_PATH } from "../features/workspace/settings/google-callback/google-callback-page";
import { TeamPage } from "../team/team-page";
import { reservedPrefixRoutes } from "./leave-console";

type Mode = "workspace" | "anywhere" | "agent";

/** Screens of later phases: permission-gated placeholders on their final routes (PLAN §1.1). */
const PLACEHOLDERS: readonly { path: string; titleKey: string; permission: PermissionKey; mode?: Mode }[] = [
  { path: CONSOLE_PATHS.dashboard, titleKey: "nav.dashboard", permission: "analytics.view" },
  { path: CONSOLE_PATHS.inbox, titleKey: "nav.inbox", permission: "inbox.view" },
  { path: CONSOLE_PATHS.analytics, titleKey: "nav.analytics", permission: "analytics.view" },
  { path: CONSOLE_PATHS.agents, titleKey: "console.chooseAgent", permission: "agents.view" },
  { path: CONSOLE_PATHS.playground, titleKey: "nav.playground", permission: "playground.use", mode: "agent" },
  { path: CONSOLE_PATHS.knowledge, titleKey: "nav.knowledge", permission: "knowledge.view", mode: "agent" },
  { path: CONSOLE_PATHS.sourceDetail, titleKey: "nav.knowledge", permission: "knowledge.view" },
  { path: CONSOLE_PATHS.agentSettings, titleKey: "nav.agent-settings", permission: "agents.edit", mode: "agent" },
  { path: CONSOLE_PATHS.actions, titleKey: "nav.actions", permission: "actions.view", mode: "agent" },
  { path: CONSOLE_PATHS.evaluations, titleKey: "nav.evaluations", permission: "evaluations.view", mode: "agent" },
  { path: CONSOLE_PATHS.widgetStudio, titleKey: "nav.widget-studio", permission: "widget.view", mode: "agent" },
  { path: CONSOLE_PATHS.webappStudio, titleKey: "nav.webapp-studio", permission: "widget.view", mode: "agent" },
  { path: CONSOLE_PATHS.channels, titleKey: "nav.channels", permission: "channels.view", mode: "agent" },
  { path: CONSOLE_PATHS.gaps, titleKey: "nav.gaps", permission: "gaps.view" },
  { path: CONSOLE_PATHS.leads, titleKey: "nav.leads", permission: "leads.view" },
  { path: CONSOLE_PATHS.workflows, titleKey: "nav.workflows", permission: "workflows.view" },
  { path: CONSOLE_PATHS.workflow, titleKey: "nav.workflows", permission: "workflows.view" },
  { path: CONSOLE_PATHS.announcements, titleKey: "nav.announcements", permission: "announcements.view" },
  { path: CONSOLE_PATHS.wallboard, titleKey: "nav.wallboard", permission: "wallboard.view" },
  { path: CONSOLE_PATHS.audiences, titleKey: "nav.audiences", permission: "audiences.view" },
  { path: CONSOLE_PATHS.billing, titleKey: "nav.billing", permission: "billing.view" },
  { path: CONSOLE_PATHS.settings, titleKey: "nav.settings", permission: "settings.view" },
];

const ROLES_HANDLE: ConsoleRouteHandle = { titleKey: "roles.title", parent: { labelKey: "nav.team", to: consoleRoutes.team } };

const workspaceChildren: RouteObject[] = [
  { index: true, element: <Navigate to="dashboard" replace /> },
  {
    path: CONSOLE_PATHS.team,
    element: (
      <RequirePermission permission="team.view">
        <TeamPage />
      </RequirePermission>
    ),
  },
  {
    path: CONSOLE_PATHS.roles,
    handle: ROLES_HANDLE,
    element: (
      <RequirePermission permission="team.view">
        <RolesPage />
      </RequirePermission>
    ),
  },
  // The User Guide is for every member: no permission gate, like its server-side nav item.
  { path: CONSOLE_PATHS.guide, element: featureScreens[CONSOLE_PATHS.guide] },
  ...PLACEHOLDERS.map((p) => ({
    path: p.path,
    element: featureScreens[p.path] ?? <GatedPlaceholder titleKey={p.titleKey} permission={p.permission} mode={p.mode} />,
  })),
  ...serviceRoutes,
  ...connectorRoutes,
  ...knowledgeGraphRoutes,
  { path: "*", element: <NotFoundPage /> },
];

export const consoleRouteTree: RouteObject[] = [
  ...reservedPrefixRoutes,
  {
    element: <PublicOnly />,
    children: [
      { path: CONSOLE_PATHS.login, element: <LoginPage /> },
      { path: CONSOLE_PATHS.register, element: <RegisterPage /> },
      { path: CONSOLE_PATHS.forgotPassword, element: <ForgotPasswordPage /> },
      { path: CONSOLE_PATHS.resetPassword, element: <ResetPasswordPage /> },
      { path: CONSOLE_PATHS.twoFactor, element: <TwoFactorPage /> },
    ],
  },
  { path: CONSOLE_PATHS.verifyEmail, element: <VerifyEmailPage /> },
  { path: CONSOLE_PATHS.acceptInvite, element: <AcceptInvitePage /> },
  {
    element: <RequireAuth />,
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: CONSOLE_PATHS.onboarding, element: <OnboardingPage /> },
      // Meta redirects to one fixed URL, so the Messenger connect callback lives outside `/w/:ws`.
      { path: META_CALLBACK_PATH, element: featureScreens[META_CALLBACK_PATH] },
      // Same for Google Calendar: one fixed redirect URL, so the callback lives outside `/w/:ws`.
      { path: GOOGLE_CALLBACK_PATH, element: featureScreens[GOOGLE_CALLBACK_PATH] },
      {
        path: CONSOLE_PATHS.workspace,
        element: <WorkspaceGate />,
        children: [{ element: <ConsoleLayout />, children: workspaceChildren }],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
];
